import React, { useEffect, useRef, useState } from 'react'
import { cn } from '~/utils/misc.tsx'
import { Spacer } from './spacer.tsx'

type Props = {
  title?: string
  children: React.ReactNode
}

export const LegalPage = ({ title, children }: Props) => {
  return (
    <div>
      {title && (
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">{title}</h1>
        </div>
      )}

      <pre className="text-secondary-blue whitespace-pre-wrap text-sm sm:text-[1rem] ">
        {children}
      </pre>
    </div>
  )
}

export function Title({
  title,
  className,
  spacer = true,
  isSubtitle = false,
}: {
  title: string
  className?: string
  spacer?: boolean
  isSubtitle?: boolean
}) {
  return (
    <>
      {spacer && <Spacer size="2xs" />}
      <div
        className={cn(
          className,
          `${isSubtitle ? 'md:text-lg' : 'text-md md:text-xl'} font-bold `,
        )}
      >
        {title}
      </div>
    </>
  )
}

type FormatListProps = {
  items: string[] | React.ReactNode[]
  style: 'numbered' | 'alphabetical' | 'symbolic' | 'dots'
  symbol?: string | React.ReactNode
  className?: string
}
export function FormatList({
  items,
  style,
  symbol,
  className,
}: FormatListProps) {
  const list = items.map((item, index) => {
    const prefix =
      style === 'numbered' ? (
        `${index + 1}.`
      ) : style === 'alphabetical' ? (
        `${String.fromCharCode('a'.charCodeAt(0) + index)}.`
      ) : style === 'dots' ? (
        <div className="mt-[6px] h-[6px] w-[6px] rounded-full bg-black"></div>
      ) : (
        symbol
      )

    return (
      <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div style={{ marginRight: '10px' }}>{prefix}</div>
        <p>{item}</p>
      </div>
    )
  })

  return <div className={cn(className, 'mt-3 grid gap-3')}>{list}</div>
}

type LegalTableProps = {
  rows: Record<string, string>[]
}
export function LegalTable({ rows }: LegalTableProps) {
  const headers = Object.keys(rows[0])
  const tableRef = useRef<HTMLTableElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const [isMobile, setIsMobile] = useState(false) //'isMobile' if table is too big for the container

  useEffect(() => {
    let tableMinimumSize: number // we must save the size of the table when this disappear because we lose the size when this is not display

    const handleResize = () => {
      const containerWidth =
        tableContainerRef.current?.getBoundingClientRect().width
      const tableWidth = tableRef.current?.getBoundingClientRect().width

      if (!containerWidth) return // this means the page has loaded

      // when resizing, if tableMinimumSize was set, an this is bigger than container, table will reappear
      if (tableMinimumSize && containerWidth > tableMinimumSize) {
        return setIsMobile(false)
      }

      if (!tableWidth) return // this will be null if the page is too small for the table

      if (tableWidth > containerWidth) {
        // in the point the table cant fit, save that size
        tableMinimumSize = tableWidth
        return setIsMobile(true)
      }
    }
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div ref={tableContainerRef} className="mt-3 w-full">
      {!isMobile ? (
        <table ref={tableRef} id="table" className={`w-full`}>
          <thead className="text-bold text-white">
            <tr>
              {headers.map(header => (
                <th
                  className={`w-[${
                    100 / headers.length
                  }%] min-w-auto border-[1px] border-gray-300 bg-foreground p-3`}
                  key={header}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map(header => (
                  <td
                    className={`${rowIndex % 2 !== 0 ? 'bg-gray-100' : ''}
                  break-words border-[1px] border-gray-300 p-2`}
                    key={header}
                  >
                    {row[header]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="max-h-[70vh] overflow-x-scroll">
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={`${rowIndex % 2 !== 0 ? 'bg-gray-100' : ''} p-3`}
            >
              {headers.map(header => (
                <div key={header} className=" py-1">
                  <p className="font-bold">{header}: </p>
                  <p className="pl-3">{row[header]}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
